import { MINDMAP_ROOT_LABEL, MINDMAP_ROOT_NODE_ID, MINDMAP_SEED_ID, MINDMAP_VERSION } from './MindmapConstants';
import type { Mindmap, MindmapLayoutDirection, MindmapNode } from './MindmapTypes';
import { getNextNodeId } from './MindmapIds';
import { deriveStyleTokensFromNode } from '@/features/mindmap/core/syntax/Tokens';
import { createLogger } from '@core/be/platform/logging/Logger';

const logger = createLogger('mindmap:state');

export function getSeedMindmap(): Mindmap {
  const rootNode: MindmapNode = {
    id: MINDMAP_ROOT_NODE_ID,
    parentId: null,
    text: MINDMAP_ROOT_LABEL,
    order: 0,
    shape: 'rectangle',
  };
  rootNode.styleTokens = deriveStyleTokensFromNode(rootNode);
  return {
    id: MINDMAP_SEED_ID,
    version: MINDMAP_VERSION,
    rootId: MINDMAP_ROOT_NODE_ID,
    nodes: [rootNode],
    layoutDirection: 'right',
  };
}

export type MindmapMode = 'select' | 'pan';

export interface MindmapUIState {
  mode: MindmapMode;
  isInlineEditing: boolean;
}

export function getInitialUIState(): MindmapUIState {
  return { mode: 'select', isInlineEditing: false };
}

export function setInlineEditing(state: MindmapUIState, editing: boolean): MindmapUIState {
  return { ...state, isInlineEditing: editing };
}

export function toggleMode(state: MindmapUIState): MindmapUIState {
  if (state.isInlineEditing) return state;
  return { ...state, mode: state.mode === 'select' ? 'pan' : 'select' };
}

export function getInteractionFlags(mode: MindmapMode) {
  // Crossed behavior per UX feedback:
  // - If we pan with the wheel (scroll), left-click should select (not pan).
  // - If we pan with left-click (drag), the wheel should zoom (not pan).
  return mode === 'select'
    ? { panOnDrag: false, selectionOnDrag: false, panOnScroll: true, zoomOnScroll: false }
    : { panOnDrag: true, selectionOnDrag: false, panOnScroll: false, zoomOnScroll: true };
}

export interface OperationResult {
  mindmap: Mindmap;
  selectedId: string;
}

function clone(m: Mindmap): Mindmap {
  return { ...m, nodes: m.nodes.map((n) => ({ ...n })) };
}

export type MindmapNodeStylePatch = Partial<Pick<MindmapNode, 'textColor' | 'bgColor' | 'borderColor' | 'shape'>>;

export interface UpdateNodeStyleOptions {
  preview?: boolean;
}

const ROOT_PARENT_KEY = '__root__';

function buildChildrenMap(nodes: MindmapNode[]): Map<string, MindmapNode[]> {
  const childrenMap = new Map<string, MindmapNode[]>();
  for (const node of nodes) {
    const key = node.parentId ?? ROOT_PARENT_KEY;
    const list = childrenMap.get(key);
    if (list) {
      list.push(node);
    } else {
      childrenMap.set(key, [node]);
    }
  }
  return childrenMap;
}

function normalizeMindmap(base: Mindmap) {
  const childrenMap = buildChildrenMap(base.nodes);
  const ordered: MindmapNode[] = [];
  const visit = (id: string) => {
    const node = base.nodes.find((n) => n.id === id);
    if (!node) return;
    ordered.push(node);
    const kids = childrenMap.get(id);
    if (!kids?.length) return;
    kids
      .sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return a.id.localeCompare(b.id);
      })
      .forEach((child, index) => {
        child.order = index;
        visit(child.id);
      });
  };

  const roots = childrenMap.get(ROOT_PARENT_KEY) ?? [];
  const rootFirst = roots.find((r) => r.id === base.rootId);
  if (rootFirst) {
    rootFirst.order = 0;
    visit(rootFirst.id);
  }

  // Append any remaining root-level nodes deterministically
  roots
    .filter((r) => r.id !== base.rootId)
    .sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.id.localeCompare(b.id);
    })
    .forEach((root, index) => {
      root.order = index + 1;
      visit(root.id);
    });

  // Include any nodes that were disconnected
  const orderedIds = new Set(ordered.map((n) => n.id));
  base.nodes
    .filter((n) => !orderedIds.has(n.id))
    .forEach((node) => ordered.push(node));

  base.nodes = ordered;
}

function getSiblings(nodes: MindmapNode[], parentId: string | null): MindmapNode[] {
  return nodes.filter((n) => n.parentId === parentId).sort((a, b) => a.order - b.order);
}

function bumpOrders(nodes: MindmapNode[], parentId: string | null, fromOrder: number) {
  for (const n of nodes) {
    if (n.parentId === parentId && n.order >= fromOrder) n.order += 1;
  }
}

export function createSiblingAfter(m: Mindmap, refId: string, label = 'New'): OperationResult {
  const base = clone(m);
  const ref = base.nodes.find((n) => n.id === refId);
  if (!ref || ref.parentId === null) return { mindmap: m, selectedId: refId };
  const newId = getNextNodeId(base);
  const order = ref.order + 1;
  bumpOrders(base.nodes, ref.parentId, order);
  // Determine depth of the new node (same as ref)
  const newDepth = (() => {
    const map = new Map(base.nodes.map((n) => [n.id, n] as const));
    let d = 0;
    let cur: MindmapNode | undefined = ref;
    while (cur?.parentId) {
      d += 1;
      cur = map.get(cur.parentId);
    }
    return d;
  })();
  const shape = newDepth === 0 ? 'rectangle' : newDepth === 1 ? 'pill' : 'underline';
  const newNode: MindmapNode = { id: newId, parentId: ref.parentId, text: label, order, shape };
  newNode.styleTokens = deriveStyleTokensFromNode(newNode);
  base.nodes.push(newNode);
  normalizeMindmap(base);
  return { mindmap: base, selectedId: newId };
}

export function createSiblingBefore(m: Mindmap, refId: string, label = 'New'): OperationResult {
  const base = clone(m);
  const ref = base.nodes.find((n) => n.id === refId);
  if (!ref || ref.parentId === null) return { mindmap: m, selectedId: refId };
  const newId = getNextNodeId(base);
  const order = ref.order;
  bumpOrders(base.nodes, ref.parentId, order);
  const newDepth = (() => {
    const map = new Map(base.nodes.map((n) => [n.id, n] as const));
    let d = 0;
    let cur: MindmapNode | undefined = ref;
    while (cur?.parentId) {
      d += 1;
      cur = map.get(cur.parentId);
    }
    return d;
  })();
  const shape = newDepth === 0 ? 'rectangle' : newDepth === 1 ? 'pill' : 'underline';
  const newNode: MindmapNode = { id: newId, parentId: ref.parentId, text: label, order, shape };
  newNode.styleTokens = deriveStyleTokensFromNode(newNode);
  base.nodes.push(newNode);
  normalizeMindmap(base);
  return { mindmap: base, selectedId: newId };
}

export function createChildLast(m: Mindmap, parentId: string, label = 'New'): OperationResult {
  const base = clone(m);
  const parent = base.nodes.find((n) => n.id === parentId);
  if (!parent) return { mindmap: m, selectedId: parentId };
  const newId = getNextNodeId(base);
  const sibs = getSiblings(base.nodes, parentId);
  const order = sibs.length;
  const parentDepth = (() => {
    const map = new Map(base.nodes.map((n) => [n.id, n] as const));
    let d = 0;
    let cur: MindmapNode | undefined = parent;
    while (cur?.parentId) {
      d += 1;
      cur = map.get(cur.parentId);
    }
    return d;
  })();
  const newDepth = parentDepth + 1;
  const shape = newDepth === 0 ? 'rectangle' : newDepth === 1 ? 'pill' : 'underline';
  const newNode: MindmapNode = { id: newId, parentId, text: label, order, shape };
  newNode.styleTokens = deriveStyleTokensFromNode(newNode);
  base.nodes.push(newNode);
  normalizeMindmap(base);
  return { mindmap: base, selectedId: newId };
}

export function createChildFirst(m: Mindmap, parentId: string, label = 'New'): OperationResult {
  const base = clone(m);
  const parent = base.nodes.find((n) => n.id === parentId);
  if (!parent) return { mindmap: m, selectedId: parentId };
  const newId = getNextNodeId(base);
  bumpOrders(base.nodes, parentId, 0);
  const parentDepth = (() => {
    const map = new Map(base.nodes.map((n) => [n.id, n] as const));
    let d = 0;
    let cur: MindmapNode | undefined = parent;
    while (cur?.parentId) {
      d += 1;
      cur = map.get(cur.parentId);
    }
    return d;
  })();
  const newDepth = parentDepth + 1;
  const shape = newDepth === 0 ? 'rectangle' : newDepth === 1 ? 'pill' : 'underline';
  const newNode: MindmapNode = { id: newId, parentId, text: label, order: 0, shape };
  newNode.styleTokens = deriveStyleTokensFromNode(newNode);
  base.nodes.push(newNode);
  normalizeMindmap(base);
  return { mindmap: base, selectedId: newId };
}

export function renameNode(m: Mindmap, nodeId: string, raw: string): Mindmap {
  const base = clone(m);
  const node = base.nodes.find((n) => n.id === nodeId);
  if (!node) return m;
  const text = raw.trim();
  if (!text) return m; // empty reverts
  node.text = text;
  return base;
}

export function updateNodeStyle(
  m: Mindmap,
  nodeId: string,
  patch: MindmapNodeStylePatch,
  options?: UpdateNodeStyleOptions
): Mindmap {
  const target = m.nodes.find((n) => n.id === nodeId);
  if (!target) return m;
  let changed = false;
  for (const key of ['textColor', 'bgColor', 'borderColor', 'shape'] as const) {
    if (Object.prototype.hasOwnProperty.call(patch, key)) {
      const value = patch[key];
      if (value !== target[key]) {
        changed = true;
        break;
      }
    }
  }
  const shouldForce = options?.preview && Object.keys(patch).length > 0;
  if (!changed && !shouldForce) return m;
  const base = clone(m);
  const node = base.nodes.find((n) => n.id === nodeId);
  if (!node) return m;
  for (const key of ['textColor', 'bgColor', 'borderColor', 'shape'] as const) {
    if (!Object.prototype.hasOwnProperty.call(patch, key)) continue;
    switch (key) {
      case 'textColor':
        node.textColor = patch.textColor;
        break;
      case 'bgColor':
        node.bgColor = patch.bgColor;
        break;
      case 'borderColor':
        node.borderColor = patch.borderColor;
        break;
      case 'shape':
        if (
          patch.shape === undefined ||
          patch.shape === 'rounded' ||
          patch.shape === 'rectangle' ||
          patch.shape === 'pill' ||
          patch.shape === 'underline'
        ) {
          node.shape = patch.shape;
        }
        break;
      default:
        break;
    }
  }
  node.styleTokens = deriveStyleTokensFromNode(node);
  return base;
}

export function updateLayoutDirection(m: Mindmap, direction: MindmapLayoutDirection): Mindmap {
  if ((m.layoutDirection ?? 'right') === direction) {
    return m;
  }
  return { ...m, layoutDirection: direction };
}

export function toggleNodeCollapse(m: Mindmap, nodeId: string, next?: boolean): Mindmap {
  const base = clone(m);
  const node = base.nodes.find((n) => n.id === nodeId);
  if (!node) return m;
  const desired = typeof next === 'boolean' ? next : !node.collapsed;
  node.collapsed = desired ? true : undefined;
  return base;
}

// Navigation helpers
export function getParentIdOf(m: Mindmap, nodeId: string): string | null {
  const n = m.nodes.find((x) => x.id === nodeId);
  return n ? n.parentId : null;
}

export function getFirstChildIdOf(m: Mindmap, nodeId: string): string | null {
  const kids = getSiblings(m.nodes, nodeId);
  return kids.length ? kids[0].id : null;
}

export function getPrevSiblingId(m: Mindmap, nodeId: string): string | null {
  const n = m.nodes.find((x) => x.id === nodeId);
  if (!n) return null;
  const sibs = getSiblings(m.nodes, n.parentId);
  const idx = sibs.findIndex((s) => s.id === nodeId);
  return idx > 0 ? sibs[idx - 1].id : null;
}

export function getNextSiblingId(m: Mindmap, nodeId: string): string | null {
  const n = m.nodes.find((x) => x.id === nodeId);
  if (!n) return null;
  const sibs = getSiblings(m.nodes, n.parentId);
  const idx = sibs.findIndex((s) => s.id === nodeId);
  return idx >= 0 && idx < sibs.length - 1 ? sibs[idx + 1].id : null;
}

/**
 * Move a node to a different parent
 */
export function moveNodeToParent(
  m: Mindmap,
  nodeId: string,
  newParentId: string | null,
  targetIndex: number
): OperationResult {
  const base = clone(m);
  const node = base.nodes.find(n => n.id === nodeId);
  if (!node) return { mindmap: m, selectedId: nodeId };
  
  // Can't move root
  if (nodeId === m.rootId) return { mindmap: m, selectedId: nodeId };
  
  // Can't move into own subtree
  const isDescendant = (candidateId: string | null, ancestorId: string): boolean => {
    if (!candidateId || candidateId === ancestorId) return true;
    const candidate = base.nodes.find(n => n.id === candidateId);
    return candidate?.parentId ? isDescendant(candidate.parentId, ancestorId) : false;
  };
  if (newParentId && isDescendant(newParentId, nodeId)) {
    return { mindmap: m, selectedId: nodeId };
  }

  const oldParentId = node.parentId;
  const oldOrder = node.order;

  // CRITICAL: Create NEW node objects instead of mutating
  const newNodes = base.nodes.map(n => {
    // The node being moved
    if (n.id === nodeId) {
      return { ...n, parentId: newParentId, order: targetIndex };
    }
    
    // Old siblings: decrement order for those after the moved node
    if (n.parentId === oldParentId && n.order > oldOrder) {
      return { ...n, order: n.order - 1 };
    }
    
    // New siblings: increment order for those at or after target index
    if (n.parentId === newParentId && n.order >= targetIndex) {
      return { ...n, order: n.order + 1 };
    }
    
    // All other nodes stay the same
    return n;
  });

  // CRITICAL: Create completely new Mindmap object to ensure React detects the change
  const result = {
    ...base,
    nodes: newNodes, // NEW array with NEW node objects
  };

  // Ensure array order reflects sibling order so ELK respects model order
  normalizeMindmap(result);

  return { mindmap: result, selectedId: nodeId };
}

/**
 * Reorder a node within its current parent
 */
export function moveNodeWithinParent(
  m: Mindmap,
  nodeId: string,
  targetIndex: number
): OperationResult {
  const base = clone(m);
  const node = base.nodes.find(n => n.id === nodeId);
  if (!node) return { mindmap: m, selectedId: nodeId };

  const parentId = node.parentId;
  const oldIndex = node.order;

  // Get siblings sorted by order
  const siblings = base.nodes
    .filter(n => n.parentId === parentId)
    .sort((a, b) => a.order - b.order);

  // Debug logging
  logger.debug('[moveNodeWithinParent] %O', {
    nodeId,
    oldIndex,
    targetIndex,
    siblings: siblings.map(s => ({ id: s.id, order: s.order })),
  });

  // Remove node from list
  const nodeIndex = siblings.findIndex(n => n.id === nodeId);
  if (nodeIndex === -1) return { mindmap: m, selectedId: nodeId };
  siblings.splice(nodeIndex, 1);

  // Insert at target index (targetIndex is in the array WITHOUT the dragged node)
  siblings.splice(targetIndex, 0, node);

  // CRITICAL: Create NEW node objects with updated order (don't mutate!)
  const updatedNodeIds = new Set(siblings.map(n => n.id));
  const newNodes = base.nodes.map(n => {
    if (!updatedNodeIds.has(n.id)) return n; // Keep non-sibling nodes as-is
    
    const newOrder = siblings.findIndex(s => s.id === n.id);
    if (newOrder === -1) return n; // Should never happen
    
    // Create NEW node object with updated order
    return { ...n, order: newOrder };
  });

  logger.debug('[moveNodeWithinParent] after: %O', siblings.map(s => ({ id: s.id, order: siblings.findIndex(x => x.id === s.id) })));
  
  // CRITICAL: Create completely new Mindmap object to ensure React detects the change
  const result = {
    ...base,
    nodes: newNodes, // NEW array with NEW node objects
  };
  
  logger.debug('[moveNodeWithinParent] returning new mindmap, base===result: %s, nodes equal? %s', base === result, base.nodes === result.nodes);

  // Ensure array order reflects sibling order so ELK respects model order
  normalizeMindmap(result);

  return { mindmap: result, selectedId: nodeId };
}

export function deleteSubtree(m: Mindmap, nodeId: string): OperationResult {
  if (nodeId === m.rootId) {
    return { mindmap: m, selectedId: m.rootId };
  }
  const base = clone(m);
  const toDelete = new Set<string>();
  const mark = (id: string) => {
    toDelete.add(id);
    for (const n of base.nodes) if (n.parentId === id) mark(n.id);
  };
  mark(nodeId);
  const victim = base.nodes.find((n) => n.id === nodeId);
  const parentId = victim?.parentId ?? m.rootId;
  const victimOrder = victim?.order ?? 0;
  base.nodes = base.nodes.filter((n) => !toDelete.has(n.id));
  // Determine selection fallback: prefer previous sibling, else next sibling, else parent
  const remainingSiblings = getSiblings(base.nodes, parentId);
  let selectedId = parentId;
  if (remainingSiblings.length > 0) {
    const prevIdx = victimOrder - 1;
    if (prevIdx >= 0 && prevIdx < remainingSiblings.length) {
      selectedId = remainingSiblings[prevIdx].id;
    } else if (victimOrder >= 0 && victimOrder < remainingSiblings.length) {
      // Next sibling falls into the removed node's former index
      selectedId = remainingSiblings[victimOrder].id;
    }
  }
  // re-pack orders among siblings under parent
  remainingSiblings.forEach((s, i) => {
    const ref = base.nodes.find((n) => n.id === s.id);
    if (ref) ref.order = i;
  });
  normalizeMindmap(base);
  return { mindmap: base, selectedId };
}
